import { useMemo } from 'react';
import { Spinner } from '@ds';
import { ResourceAvatar } from '@ui';
import { ReservationCondition } from '@lp/features/AssignmentTables/components';
import { CSAssignmentCandidate, Employee, RSAssignmentCandidate } from '@/api/apis/resources';
import { OrganType } from '@/api/apis/case';
import { queryClient } from '@/queries';
import { hubQueries } from '@/queries/hub';
import { getFullName } from '@/utils/helpers';
import { ResourceCard, ResourceCardText, ResourceWarningTooltip } from './ResourceCard';

const getEmployeeSubtitle = (
  employee: RSAssignmentCandidate | CSAssignmentCandidate | null,
  type: 'retrieval-surgeon' | 'clinical-specialist',
) => {
  switch (type) {
    case 'clinical-specialist':
      if (!employee) return 'Clinical Specialist';

      const csData = employee as CSAssignmentCandidate;
      const currentShiftHub = csData.shiftHubId
        ? queryClient.getQueryData(hubQueries.item(csData.shiftHubId).queryKey)
        : undefined;

      return ['Clinical Specialist', currentShiftHub?.code].filter(Boolean).join(', ');
    case 'retrieval-surgeon':
      if (!employee) return 'Surgeon';

      const rsData = employee as RSAssignmentCandidate;

      return ['Surgeon', rsData.homeLocation?.city, rsData.homeLocation?.state || rsData.homeLocation?.country]
        .filter(Boolean)
        .join(', ');
    default:
      return '';
  }
};

const getWarningTooltipContent = (
  employee: RSAssignmentCandidate | CSAssignmentCandidate | null,
  type: 'retrieval-surgeon' | 'clinical-specialist',
  orTime: string,
  caseName?: string,
) => {
  switch (type) {
    case 'clinical-specialist':
      if (!employee) return 'This Clinical Specialist is no longer available for reservation.';

      const csData = employee as CSAssignmentCandidate;
      return (
        <ReservationCondition
          lastCertificationAtHubInfo={csData.lastCertificationAtHubInfo}
          conflictReservation={csData.conflictedReservation}
          conflictRest={csData.conflictedRest}
          match={csData.match}
          onCallAfterTargetMinutes={csData.onCallAfterTargetMinutes}
          onCallBeforeTargetMinutes={csData.onCallBeforeTargetMinutes}
          onRestEndsBeforeTargetMinutes={csData.onRestEndsBeforeTargetMinutes}
          onRestStartsAfterTargetMinutes={csData.onRestStartsAfterTargetMinutes}
          caseName={caseName}
        />
      );
    case 'retrieval-surgeon':
      if (!employee) return 'This Retrieval Surgeon is no longer available for reservation.';

      const rsData = employee as RSAssignmentCandidate;
      return (
        <ReservationCondition
          unavailabilities={rsData.unavailabilities}
          orTime={orTime}
          conflictReservation={rsData.conflictedReservation}
          conflictRest={rsData.conflictedRest}
          match={rsData.match}
          onCallAfterTargetMinutes={rsData.onCallAfterTargetMinutes}
          onCallBeforeTargetMinutes={rsData.onCallBeforeTargetMinutes}
          onRestEndsBeforeTargetMinutes={rsData.onRestEndsBeforeTargetMinutes}
          caseName={caseName}
        />
      );
    default:
      return null;
  }
};

type SuggestedEmployeeInfoProps = {
  isLoading: boolean;
  isWarningState: boolean;
  employee: RSAssignmentCandidate | CSAssignmentCandidate | null;
  fallbackInfo: Pick<Employee, 'id' | 'firstName' | 'lastName' | 'profilePhoto'> | null;
  organType: OrganType;
  type: 'retrieval-surgeon' | 'clinical-specialist';
  orTimeUTC: string;
  caseName?: string;
};

export const SuggestedEmployeeInfo = ({
  isLoading,
  isWarningState,
  employee,
  fallbackInfo,
  type,
  organType,
  orTimeUTC,
  caseName,
}: SuggestedEmployeeInfoProps) => {
  const subTitle = getEmployeeSubtitle(employee, type);
  const warningTooltipContent = getWarningTooltipContent(employee, type, orTimeUTC, caseName);

  const employeeInfo = useMemo(() => {
    if (!employee && fallbackInfo) {
      return {
        employeeFullName: getFullName(fallbackInfo.firstName, fallbackInfo.lastName),
        employeeProfilePhoto: fallbackInfo.profilePhoto,
      };
    }

    return employee;
  }, [employee, fallbackInfo]);

  const showWarningState = isWarningState && !isLoading;

  return (
    <ResourceCard hasWarning={showWarningState} className="justify-center">
      {isLoading && <Spinner size="md" />}
      {!isLoading && employeeInfo && (
        <div className="flex gap-2 grow">
          <ResourceAvatar type={type} size="md" radius="md" organ={organType} src={employeeInfo.employeeProfilePhoto} />
          <ResourceCardText
            title={employeeInfo.employeeFullName}
            titleAdornment={isWarningState && <ResourceWarningTooltip content={warningTooltipContent} />}
            subTitle={subTitle}
          />
        </div>
      )}
    </ResourceCard>
  );
};
